const LINE_HEAD_SYMBOLS = [
  '- [x]',
  '- [ ]',
  '*',
  '+',
  '-',
];

/**
 * 行頭に記号が付くスタイルを形成。すでに記号がついている場合は置き換える
 * @param {string} str 
 * @param {string} symbol 
 */
const replace_line_head = (str, symbol) => {
  const WHITE_SPACE = ' ';
  // 前方の空白とそれ以降を取り出す
  const [, spaces = '', trimmed = ''] = str.match(/^(\s*)(.*)$/) || [];
  // 行頭に記号がついているかチェック
  let found_symbol = LINE_HEAD_SYMBOLS.find(sym => trimmed.indexOf(sym) === 0);
  // 更に # と 1. のパターンを調べる
  if (!found_symbol) {
    const patterns = [
      /^(#+)\s/,
      /^(\d+\.)\s/,
    ];
    patterns.forEach(pattern => {
      const matched = trimmed.match(pattern);
      if (matched) {
        found_symbol = matched[1];
      }
    });
  }
  if (found_symbol) {
    // 記号が見つかった場合は置き換えて返す
    return `${spaces}${symbol}${WHITE_SPACE}${trimmed.substring(found_symbol.length).trim()}`;
  }
  else {
    return `${spaces}${symbol}${WHITE_SPACE}${trimmed}`;
  }
};

export default class EditorUtil {
  /**
   * 
   * @param {CodeMirror.Editor} editor
   */
  constructor(editor) {
    this.editor = editor;
  }

  // カーソル位置を取得
  getCursor(start = 'from') {
    return this.editor.getCursor(start);
  }

  // 選択範囲の最後のカーソル位置を取得
  getCursorEnd() {
    return this.getCursor('to');
  }

  getSelection() {
    return this.editor.getSelection();
  }

  // カーソル位置を設定
  setCursor(pos) {
    this.editor.setCursor(pos);
  }

  // カーソル位置を行頭に移動
  setCursorToLineStart() {
    this.setCursor({ ch: 0, line: this.getCursor().line });
  }

  // カーソル位置を行末に移動
  setCursorToLineEnd() {
    const line = this.getCursor().line;
    this.setCursor({ ch: this.editor.getLine(line).length, line });
  }

  // 指定の文字数分、行数分カーソルを移動
  moveCursor(ch = 0, line = 0) {
    const cursor = this.getCursor();
    this.setCursor({ ch: cursor.ch + ch, line: cursor.line + line });
  }

  // 現在のカーソルの位置に文字を挿入
  insertTextAtCursor(text) {
    this.editor.replaceRange(text, this.getCursor());
  }

  // カーソルを末尾にあてる
  setCursorToLast() {
    this.setCursor({ ch: Infinity, line: Infinity });
  }

  // 現在の cursor から指定文字を基準に範囲選択
  setSelectionByString(str = '', direction = 'left') {
    const splitted = str.split('\n');
    const cursor = this.getCursor();
    const move_rate = direction === 'left' ? -1 : 1;
    // 一行のとき
    if (splitted.length === 1) {
      this.editor.setSelection({ line: cursor.line, ch: cursor.ch + str.length * move_rate }, cursor);
    }
    else {
      // 複数行のとき
      const last_line = direction === 'left' ? splitted[splitted.length - 1] : splitted[0];
      const last_line_length = last_line.length;
      this.editor.setSelection({ line: cursor.line + (splitted.length - 1) * move_rate, ch: last_line_length * move_rate }, cursor);
    }
  }

  // 記号が文字を囲うスタイルを形成
  replaceEnclosing(symbol) {
    // 選択中の文字を取得
    const selection = this.getSelection();
    // 形成
    const formatted_text = symbol + selection + symbol;
    // テキストを置き換える
    this.editor.replaceSelection(formatted_text);
    this.editor.focus();

    requestAnimationFrame(() => {
      // 改行している場合はカーソルを末尾にあてる
      if (/\r?\n/.test(formatted_text)) {
        this.setCursorToLast();
      }
      else {
        // cursor を左に symbol の文字数分移動
        this.moveCursor(-symbol.length);
        // 記号を取り除いたテキストを選択状態にする
        this.setSelectionByString(selection);
      }
    });
  }

  // コードブロックを形成
  replaceCodeBlock() {
    //- 選択中の文字を取得
    const selection = this.getSelection();
    //- 改行している場合
    if (/\r?\n/.test(selection)) {
      //- コードブロック下の行の数
      const UNDER_SYMBOL_LINE_NUMBER = 1;
      const CODE_BLOCK_SYMBOL = '```';
      const cursor = this.getCursor();
      const from = { ch: 0, line: cursor.line };
      const to = this.getCursorEnd();
      to.ch = this.editor.getLine(to.line).length;
      // 中途半端な位置を選択していてもちゃんと最初の行の行頭から最後の行の行末の範囲から取得する
      const range_string = this.editor.getRange(from, to);
      //- テキストの上下を記号で囲むように形成
      const formatted_text = `${CODE_BLOCK_SYMBOL}\n${range_string}\n${CODE_BLOCK_SYMBOL}`;
      //- テキストを置き換える
      this.editor.replaceRange(formatted_text, from, to);
      this.editor.focus();

      requestAnimationFrame(() => {
        // 前の行末をへカーソルを移動
        this.moveCursor(Infinity, -UNDER_SYMBOL_LINE_NUMBER);
        // 記号を取り除いたテキストを選択状態にする
        this.setSelectionByString(selection);
      });
    }
    //- 改行していないもしくは、文字を選択していない場合
    else {
      this.replaceEnclosing('`');
    }
  }

  // 文字の頭に記号が付くスタイルを形成
  replaceLineHead(symbol) {
    //- 選択中の文字を取得
    const selection = this.getSelection();
    //- 選択文字がない場合
    if (!selection) {
      const cursor = this.getCursor();
      const line_string = this.editor.getLine(cursor.line);
      const from = { ch: 0, line: cursor.line };
      const to = { ch: line_string.length, line: cursor.line };
      // すでに記号がついている場合を考慮して置換する
      const formatted_text = replace_line_head(this.editor.getRange(from, to), symbol);
      this.editor.replaceRange(formatted_text, from, to);
      this.editor.focus();
    }
    //- 文字選択している場合
    else {
      const cursor = this.getCursor();
      const from = { ch: 0, line: cursor.line };
      const to = this.getCursorEnd();
      // 中途半端な位置を選択していてもちゃんと行頭の範囲から取得する
      const range_string = this.editor.getRange(from, to);

      //- 形成(空白の行や空文字の行は記号を付けない)
      const formatted_text = range_string.split('\n').map(s => {
        if (s.trim()) return replace_line_head(s, symbol);
        return '';
      }).join('\n');

      //- テキストを置き換える
      this.editor.replaceRange(formatted_text, from, to);
      //- アイコン押したときにblurするので再度フォーカスする
      this.editor.focus();
      requestAnimationFrame(() => {
        //- 範囲の最後にカーソルを移動
        this.moveCursor(Infinity);
      });
    }
  }

  // リンク挿入のスタイルを形成
  replaceMarkdownLink(symbol) {
    //- 選択中の文字を取得
    const selection = this.getSelection();
    const INSERT_POSITION = 1;
    //- 選択文字がない場合
    if (!selection) {
      //- カーソルの位置に記号を挿入する
      this.insertTextAtCursor(symbol);
      this.editor.focus();
      // 各カッコの前にカーソルを移動
      this.moveCursor(-symbol.length + INSERT_POSITION);
    }
    //- 文字選択している場合
    else {
      const DEFAULT_URL = 'https://~';
      //- 閉じ括弧の数
      const CLOSING_PARENTHESIS_NUMBER = 1;
      //- urlの文字を選択状態にする
      let formatted_text = `[${selection}](${DEFAULT_URL})`;
      //- テキストを置き換える
      this.editor.replaceSelection(formatted_text);
      //- アイコン押したときにblurするので再度フォーカスする
      this.editor.focus();
      requestAnimationFrame(() => {
        // カーソルをカッコの前に移動
        this.moveCursor(-CLOSING_PARENTHESIS_NUMBER);
        //- DEFAULT_URL の箇所を選択状態にする
        this.setSelectionByString(DEFAULT_URL);
      });
    }
  }

  // 行を上に移動
  swapLineUp() {
    const cm = this.editor;
    // 読み取り専用なら何もしない
    if (cm.isReadOnly && cm.isReadOnly()) return;
    // 選択範囲を取得
    const ranges = cm.listSelections();
    const lines_to_move = [];
    // 直前に処理した行の終端(重複判定用)
    let at = cm.firstLine() - 1;
    // 移動後に復元する選択範囲
    const new_sels = [];

    // 上方向は前から処理
    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      // これ以上上に移動できない場合はスキップ
      if (range.from().line <= cm.firstLine()) {
        // 移動できない範囲はそのまま保持
        new_sels.push({
          anchor: { line: range.anchor.line, ch: range.anchor.ch },
          head: { line: range.head.line, ch: range.head.ch },
        });
        continue;
      }
      // 選択範囲の直前の行から移動対象にする
      const from = range.from().line - 1;
      let to = range.to().line;
      // 選択位置を1行上にずらして保存
      new_sels.push({
        anchor: { line: range.anchor.line - 1, ch: range.anchor.ch },
        head: { line: range.head.line - 1, ch: range.head.ch },
      });
      // 直前の範囲と重ならないなら新規で追加
      if (from > at) {
        lines_to_move.push(from, to);
      }
      // 連続する範囲ならその分拡張
      else if (lines_to_move.length) {
        lines_to_move[lines_to_move.length - 1] = to;
      }
      // 最後に処理した行の終端を更新
      at = to;
    }
    // まとめて編集
    cm.operation(() => {
      // [from, to] のペアで詰める配列なので、2要素ずつ処理
      const LINE_PAIR_SIZE = 2;
      // 一意変更を識別するための文字列
      const SWAP_LINE_ORIGIN = '+swapLine';

      // 上方向は前から処理
      for (let i = 0; i < lines_to_move.length; i += LINE_PAIR_SIZE) {
        const from = lines_to_move[i];
        const to = lines_to_move[i + 1];
        // 移動元の行テキストを取得
        const line = cm.getLine(from);
        // 移動元の行を一旦削除
        cm.replaceRange('', { line: from, ch: 0 }, { line: from + 1, ch: 0 }, SWAP_LINE_ORIGIN);
        // 末尾を超える場合は末尾に追加
        if (to > cm.lastLine()) {
          const last = cm.lastLine();
          const last_ch = cm.getLine(last).length;
          cm.replaceRange(`\n${line}`, { line: last, ch: last_ch }, null, SWAP_LINE_ORIGIN);
        }
        // 通常は対象行の手前に挿入
        else {
          cm.replaceRange(`${line}\n`, { line: to, ch: 0 }, null, SWAP_LINE_ORIGIN);
        }
      }
      // 選択範囲を移動後の位置に復元
      cm.setSelections(new_sels);
      cm.scrollIntoView();
    });
  }

  // 行を下に移動
  swapLineDown() {
    const cm = this.editor;
    // 読み取り専用なら何もしない
    if (cm.isReadOnly && cm.isReadOnly()) return;
    // 選択範囲を取得
    const ranges = cm.listSelections();
    const lines_to_move = [];
    // 直前に処理した行の始端(重複判定用)
    let at = cm.lastLine() + 1;

    // 下方向は後ろから処理
    for (let i = ranges.length - 1; i >= 0; i--) {
      const range = ranges[i];
      let from = range.to().line + 1;
      const to = range.from().line;
      // 直前の範囲と重ならないなら新規で追加
      if (from < at) {
        lines_to_move.push(from, to);
      }
      // 連続する範囲ならその分拡張
      else if (lines_to_move.length) {
        lines_to_move[lines_to_move.length - 1] = to;
      }
      // 最後に処理した行の始端を更新
      at = to;
    }
    // まとめて編集
    cm.operation(() => {
      // [from, to] のペアで詰める配列なので、2要素ずつ処理
      const LINE_PAIR_SIZE = 2;
      // 一意変更を識別するための文字列
      const SWAP_LINE_ORIGIN = '+swapLine';

      // 下方向は後ろから処理
      for (let i = lines_to_move.length - LINE_PAIR_SIZE; i >= 0; i -= LINE_PAIR_SIZE) {
        const from = lines_to_move[i];
        const to = lines_to_move[i + 1];
        // 移動元の行テキストを取得
        const line = cm.getLine(from);
        // 最終行の場合は前行末〜最終行末まで削除
        if (from === cm.lastLine()) {
          const prev_line = from - 1;
          const prev_ch = cm.getLine(prev_line).length;
          const last_ch = cm.getLine(from).length;
          cm.replaceRange('', { line: prev_line, ch: prev_ch }, { line: from, ch: last_ch }, SWAP_LINE_ORIGIN);
        }
        // 通常は1行ぶん削除
        else {
          cm.replaceRange('', { line: from, ch: 0 }, { line: from + 1, ch: 0 }, SWAP_LINE_ORIGIN);
        }
        // 対象行の手前に挿入
        cm.replaceRange(`${line}\n`, { line: to, ch: 0 }, null, SWAP_LINE_ORIGIN);
      }
      cm.scrollIntoView();
    });
  }

  // 次の一致を選択
  selectNextOccurrence() {
    const cm = this.editor;
    const ranges = cm.listSelections();
    const selections = cm.getSelections();

    // 探索対象の文字列
    let target_text = '';
    // 最短の選択文字列を採用する
    let min_length = Infinity;
    for (let i = 0; i < selections.length; i++) {
      const text = selections[i];
      if (!text) continue;
      if (text.length < min_length) {
        min_length = text.length;
        target_text = text;
      }
    }

    // 選択がない場合はカーソル次の単語を選択(vsCodeの挙動に合わせる)
    if (!target_text) {
      const word_range = cm.findWordAt(cm.getCursor());
      if (word_range && !word_range.empty()) {
        cm.setSelection(word_range.from(), word_range.to());
      }
      return;
    }

    // 次の一致を探索するカーソルを作成
    const base_range = ranges[ranges.length - 1];
    const cursor = cm.getSearchCursor(target_text, base_range.to());
    // 次の一致がなければ終了
    if (!cursor.findNext()) return;

    const next_selections = ranges.map(range => ({ anchor: range.anchor, head: range.head }));
    next_selections.push({ anchor: cursor.from(), head: cursor.to() });
    cm.setSelections(next_selections);
    // 新しい選択が見えるようにスクロール
    cm.scrollIntoView({ from: cursor.from(), to: cursor.to() });
  }

  // テーブルをMarkdown形式で挿入
  insertMarkdownTable(rows, cols, headerString = 'Header') {
    // 初期化
    let table_string = '\n|';

    // ヘッダーの生成
    for (let i = 0; i < cols; i++) {
      // headerString + ヘッダー番号（1からスタート）を追加
      table_string += ` ${headerString}${i + 1} |`;
    }
    // ヘッダー行の終了と次の行への移行
    table_string += '\n|';

    // ヘッダーと内容を区切る線を生成
    for (let i = 0; i < cols; i++) {
      table_string += '--------|';
    }

    // 行の生成
    for (let i = 0; i < rows; i++) {
      // 新しい行の開始
      table_string += '\n|';

      // 列の生成
      for (let j = 0; j < cols; j++) {
        // 列の追加
        table_string += '-|';
      }
    }

    // テーブルをカーソル位置に挿入
    this.insertTextAtCursor(table_string);
  }
}
